import { useState, useMemo } from 'react';
import { Calendar, momentLocalizer, View, Event } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './BigCalendarStyles.css';
import { CalendarEvent } from '../../types';

const localizer = momentLocalizer(moment);

interface BigCalendarComponentProps {
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onSlotClick?: (slotInfo: { start: Date; end: Date }) => void;
}

interface BigCalendarEvent extends Event {
  resource: {
    originalEvent: CalendarEvent;
    type: string;
    color: string;
  };
}

export default function BigCalendarComponent({ 
  events, 
  onEventClick, 
  onSlotClick 
}: BigCalendarComponentProps) {
  const [view, setView] = useState<View>('month');
  const [date, setDate] = useState(new Date());

  // Convert CalendarEvent to react-big-calendar Event format
  const calendarEvents: BigCalendarEvent[] = useMemo(() => {
    return events.map((event) => {
      // Parse the date
      const eventDate = new Date(event.date);
      
      // Parse start time from the event.time field (e.g., "09:00")
      const [startHours, startMinutes] = event.time.split(':').map(Number);
      
      // Create start time
      const start = new Date(eventDate);
      start.setHours(startHours, startMinutes, 0, 0);
      
      // Calculate end time - use endTime if available, otherwise fallback to duration
      const end = new Date(eventDate);
      
      if (event.endTime) {
        // Use the actual end time from the DNS policy (e.g., "17:00")
        const [endHours, endMinutes] = event.endTime.split(':').map(Number);
        end.setHours(endHours, endMinutes, 0, 0);
      } else if (event.duration) {
        // Fallback to duration parsing if no endTime
        const durationStr = event.duration.toLowerCase();
        
        if (durationStr.includes('hour') || durationStr.includes('h')) {
          const hoursMatch = durationStr.match(/(\d+(?:\.\d+)?)\s*h/);
          if (hoursMatch) {
            const hours = parseFloat(hoursMatch[1]);
            end.setTime(start.getTime() + (hours * 60 * 60 * 1000));
          }
        } else if (durationStr.includes('minute') || durationStr.includes('m')) {
          const minutesMatch = durationStr.match(/(\d+)\s*m/);
          if (minutesMatch) {
            const minutes = parseInt(minutesMatch[1]);
            end.setTime(start.getTime() + (minutes * 60 * 1000));
          }
        } else {
          // Default to 1 hour
          end.setTime(start.getTime() + (60 * 60 * 1000));
        }
      } else {
        // Default to 1 hour if no endTime or duration
        end.setTime(start.getTime() + (60 * 60 * 1000));
      }

      return {
        id: event.id,
        title: event.title,
        start,
        end,
        resource: {
          originalEvent: event,
          type: event.type,
          color: event.color
        }
      };
    });
  }, [events]);

  // Event style getter - no gradients, solid colors only
  const eventStyleGetter = (event: BigCalendarEvent) => {
    const color = event.resource.color || '#8B5CF6';
    
    return {
      style: {
        backgroundColor: color,
        borderRadius: '4px',
        opacity: 0.9,
        color: '#FFFFFF',
        border: `1px solid ${color}`,
        fontSize: '12px',
        fontWeight: '500',
        padding: '2px 4px'
      }
    };
  };

  // Day style getter for consistent theming
  const dayPropGetter = (date: Date) => {
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    return {
      style: {
        backgroundColor: isToday ? 'rgba(0, 245, 160, 0.1)' : 'transparent',
        color: '#F3F6FA'
      }
    };
  };

  const handleEventClick = (event: BigCalendarEvent) => {
    if (onEventClick) {
      onEventClick(event.resource.originalEvent);
    }
  };

  const handleSlotClick = (slotInfo: { start: Date; end: Date; action: string }) => {
    if (onSlotClick && slotInfo.action === 'click') {
      onSlotClick(slotInfo);
    }
  };

  const formats = {
    timeGutterFormat: 'HH:mm',
    eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) => 
      `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`,
    agendaTimeFormat: 'HH:mm',
    agendaTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) => 
      `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`
  };

  return (
    <div className="big-calendar-container">
      <Calendar
        localizer={localizer}
        events={calendarEvents}
        startAccessor="start"
        endAccessor="end"
        titleAccessor="title"
        view={view}
        onView={setView}
        date={date}
        onNavigate={setDate}
        views={['month', 'week', 'day', 'agenda']}
        eventPropGetter={eventStyleGetter}
        dayPropGetter={dayPropGetter}
        onSelectEvent={handleEventClick}
        onSelectSlot={handleSlotClick}
        selectable
        popup
        step={30}
        timeslots={2}
        formats={formats}
        style={{ 
          height: '600px',
          backgroundColor: 'transparent',
          color: '#F3F6FA'
        }}
        messages={{
          next: 'Next',
          previous: 'Previous',
          today: 'Today',
          month: 'Month',
          week: 'Week',
          day: 'Day',
          agenda: 'Agenda',
          date: 'Date',
          time: 'Time',
          event: 'Event',
          allDay: 'All Day',
          noEventsInRange: 'No DNS policies scheduled for this time range',
          showMore: (total: number) => `+${total} more`
        }}
      />
    </div>
  );
}